import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Plus, X, PencilSimple, Trash, Spinner, Cube, DotsSixVertical } from "@phosphor-icons/react";
import { toast } from "sonner";

const BUILT_IN = ["vehicle", "equipment", "trailer", "tool"];

export default function CustomTypesManager() {
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {key?, label, fields:[{label}]}
  const [delKey, setDelKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = () => api.get("/asset-types").then(({ data }) => { setTypes(data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const custom = Object.entries(types).filter(([k, v]) => !BUILT_IN.includes(k) && v?.custom);

  const openNew = () => setEditing({ label: "", fields: [{ label: "", type: "text", options: "" }] });
  const openEdit = (key, cfg) => setEditing({ key, label: cfg.label, fields: (cfg.fields || []).map((f) => ({ key: f.key, label: f.label, type: f.type || "text", options: (f.options || []).join(", ") })) });

  const save = async () => {
    const label = (editing.label || "").trim();
    if (!label) { toast.error("Enter a type name"); return; }
    const fields = editing.fields.filter((f) => (f.label || "").trim()).map((f) => {
      const out = { key: f.key, label: f.label.trim(), type: f.type || "text" };
      if (out.type === "select") out.options = (f.options || "").split(",").map((o) => o.trim()).filter(Boolean);
      return out;
    });
    if (!fields.length) { toast.error("Add at least one field"); return; }
    if (fields.some((f) => f.type === "select" && (!f.options || !f.options.length))) { toast.error("Dropdown fields need at least one option"); return; }
    setSaving(true);
    try {
      await api.post("/company/asset-types", { label, fields }, { params: editing.key ? { key: editing.key } : {} });
      toast.success(editing.key ? "Type updated" : "Type created");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/company/asset-types/${delKey}`);
      toast.success("Type deleted");
      setDelKey(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setField = (i, patch) => setEditing((ed) => ({ ...ed, fields: ed.fields.map((f, idx) => idx === i ? { ...f, ...patch } : f) }));
  const addField = () => setEditing((ed) => ({ ...ed, fields: [...ed.fields, { label: "", type: "text", options: "" }] }));
  const rmField = (i) => setEditing((ed) => ({ ...ed, fields: ed.fields.filter((_, idx) => idx !== i) }));
  const moveField = (from, to) => setEditing((ed) => {
    if (from === to || from == null || to == null) return ed;
    const f = [...ed.fields];
    const [m] = f.splice(from, 1);
    f.splice(to, 0, m);
    return { ...ed, fields: f };
  });

  if (loading) return <div className="flex justify-center py-10"><Spinner size={24} className="animate-spin text-emerald-600" /></div>;

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-6 space-y-5" data-testid="custom-types-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-500">Built-in types are always available. Create your own to track anything else.</p>
        <Button onClick={openNew} className="h-10 gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="add-custom-type"><Plus size={16} weight="bold" /> New Type</Button>
      </div>

      <div>
        <Label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Built-in</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {BUILT_IN.filter((k) => types[k]).map((k) => (
            <Badge key={k} variant="outline" className="rounded-md h-8 px-3 text-sm gap-1.5"><Cube size={14} /> {types[k].label}</Badge>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">Your custom types</Label>
        {custom.length === 0 ? (
          <p className="text-sm text-zinc-400 mt-2">No custom types yet.</p>
        ) : (
          <div className="mt-2 space-y-2">
            {custom.map(([key, cfg]) => (
              <div key={key} className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2" data-testid={`custom-type-${key}`}>
                <div>
                  <p className="font-semibold text-sm">{cfg.label}</p>
                  <p className="text-xs text-zinc-500">{(cfg.fields || []).map((f) => f.label).join(" · ")}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(key, cfg)} data-testid={`edit-type-${key}`}><PencilSimple size={16} /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => setDelKey(key)} data-testid={`delete-type-${key}`}><Trash size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="custom-type-dialog">
          <DialogHeader><DialogTitle>{editing?.key ? "Edit Asset Type" : "New Asset Type"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 py-1">
              <div>
                <Label className="text-sm font-semibold">Type name</Label>
                <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="e.g. Boat, Container, Golf Cart" className="h-11 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="custom-type-label" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Fields</Label>
                <p className="text-xs text-zinc-400 mb-1.5">Drag the handle to reorder — this sets the order shown on the asset form.</p>
                <div className="space-y-2 mt-1.5">
                  {editing.fields.map((f, i) => (
                    <div
                      key={i}
                      className={`space-y-1.5 rounded-md border p-2 transition-colors ${dragIdx === i ? "border-emerald-400 bg-emerald-50/50" : "border-zinc-100"}`}
                      draggable
                      onDragStart={() => setDragIdx(i)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => { moveField(dragIdx, i); setDragIdx(null); }}
                      onDragEnd={() => setDragIdx(null)}
                      data-testid={`custom-field-row-${i}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="cursor-grab active:cursor-grabbing text-zinc-300 hover:text-zinc-500 shrink-0" title="Drag to reorder" data-testid={`custom-field-drag-${i}`}><DotsSixVertical size={18} weight="bold" /></span>
                        <Input value={f.label} onChange={(e) => setField(i, { label: e.target.value })} placeholder={`Field ${i + 1} (e.g. Hull ID)`} className="h-10 focus:ring-2 focus:ring-emerald-500" data-testid={`custom-field-${i}`} />
                        <Select value={f.type || "text"} onValueChange={(v) => setField(i, { type: v })}>
                          <SelectTrigger className="h-10 w-32 shrink-0" data-testid={`custom-field-type-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text">Text</SelectItem>
                            <SelectItem value="number">Number</SelectItem>
                            <SelectItem value="date">Date</SelectItem>
                            <SelectItem value="select">Dropdown</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-zinc-400 hover:text-red-500 shrink-0" onClick={() => rmField(i)}><X size={16} weight="bold" /></Button>
                      </div>
                      {f.type === "select" && (
                        <Input value={f.options || ""} onChange={(e) => setField(i, { options: e.target.value })} placeholder="Options, comma separated (e.g. Small, Medium, Large)" className="h-9 text-sm focus:ring-2 focus:ring-emerald-500" data-testid={`custom-field-options-${i}`} />
                      )}
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" onClick={addField} className="h-9 gap-1.5 border-zinc-300 mt-2" data-testid="add-custom-field"><Plus size={14} /> Add field</Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-zinc-300">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="save-custom-type">{saving ? <Spinner size={16} className="animate-spin" /> : "Save Type"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delKey} onOpenChange={(o) => !o && setDelKey(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this asset type?</AlertDialogTitle><AlertDialogDescription>This removes the type from your list. Assets already using it are unaffected, but you can only delete a type when no assets use it.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-type">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
